import type { GQL_ID } from '@@partner:types:graphql'
import type {
  ShopCart,
  ShopCartHolder,
  ShopCartProduct
} from '@@partner:types:app'

import { useEffect, useMemo, useState, useCallback } from 'react'
import { partition } from 'rambda'
import { mergeLeft } from 'merge-left-utils'

const DEFAULT_SHOP_CART: Partial<ShopCart> = {
  shop_id: undefined,
  products: []
}

const DEFAULT_CART_HOLDER: ShopCartHolder = {
  carts: []
}

export interface CartFacade {
  carts: ShopCart[]
  setCarts(carts: ShopCart[]): void
  currentShopCart: ShopCart
  currentShopID?: GQL_ID
  setShopID(id?: GQL_ID): void
  setCurrentCartProducts(products: ShopCartProduct[]): void
  clearCurrentCart(): void
  addToCurrentCart(product_id: GQL_ID, amount: number, total?: number): void
  updateAmount(
    product_id: GQL_ID,
    shop_id: GQL_ID,
    amount: number,
    total?: number
  ): void
  deleteFromCurrentCart(product_id: GQL_ID): void
}

export const useCart = (shop_id: GQL_ID | undefined) => {
  const [ cartsHolder, setCartsHolder ] = useState<ShopCartHolder>(
    DEFAULT_CART_HOLDER
  )
  const [ currentShopID, setCurrentShopID ] = useState<GQL_ID | undefined>(
    shop_id
  )

  useEffect(() => {
    setCurrentShopID(shop_id)
  }, [ shop_id ])

  const currentShopCart = useMemo(
    () =>
      cartsHolder.carts.find(
        (c) => Number(c.shop_id) === Number(currentShopID)
      ) ??
      (mergeLeft(DEFAULT_SHOP_CART, { shop_id: currentShopID }) as ShopCart),
    [ cartsHolder, currentShopID ]
  )

  const setCarts = useCallback(
    (carts) => {
      setCartsHolder((old) => ({
        ...old,
        carts
      }))
    },
    [ setCartsHolder ]
  )

  const setCurrentCartProducts = useCallback(
    (products: ShopCartProduct[]) => {
      if (shop_id) {
        const cart: ShopCart = {
          shop_id,
          products
        }

        setCartsHolder((old) => ({
          ...old,
          carts: [
            ...old.carts.filter(
              (cart) => Number(cart.shop_id) !== Number(shop_id)
            ),
            cart
          ]
        }))
      }
    },
    [ shop_id ]
  )

  const clearCurrentCart = useCallback(() => {
    setCartsHolder((old) => ({
      ...old,
      carts: old.carts.filter(
        (cart) => Number(cart.shop_id) !== Number(shop_id)
      )
    }))
  }, [ shop_id ])

  const addToCurrentCart = useCallback(
    (id: GQL_ID, amount: number, total?: number) => {
      const [ [ currentCart ], otherCarts ] = partition(
        (cart) => cart.shop_id === currentShopID,
        cartsHolder.carts
      )

      if (!currentCart && currentShopID) {
        setCartsHolder((old) => ({
          ...old,
          carts: [
            ...otherCarts,
            {
              shop_id: currentShopID,
              products: [
                {
                  id,
                  amount,
                  total
                }
              ]
            }
          ]
        }))
      }
      else {
        const existingProduct = currentCart.products.find(
          (product) => product.id === id
        )

        if (existingProduct) {
          currentCart.products = currentCart.products.map((product) => {
            if (product.id === id) {
              return mergeLeft(product, {
                amount,
                total: total ?? product.total
              })
            }
            return product
          })
        }
        else {
          currentCart.products = [
            ...currentCart.products,
            {
              id,
              amount,
              total
            }
          ]
        }

        setCartsHolder((_old) => ({
          carts: [ ...otherCarts, currentCart ]
        }))
      }
    },
    [ cartsHolder.carts, currentShopID ]
  )

  const updateAmount = useCallback((id, shop_id, amount, total) => {
    setCartsHolder((old) => ({
      carts: old.carts.map((cart: ShopCart) => {
        if (cart.shop_id === shop_id) {
          return {
            ...cart,
            products: cart.products.map((product) => {
              if (product.id === id) {
                return mergeLeft(product, { amount, total })
              }
              else return product
            })
          }
        }
        return cart
      })
    }))
  }, [])

  const deleteFromCurrentCart = useCallback(
    (id: number) => {
      setCartsHolder((old) => ({
        carts: old.carts.map((cart: ShopCart) => {
          if (Number(cart.shop_id) === Number(shop_id)) {
            return {
              ...cart,
              products: cart.products.filter((product) => product.id !== id)
            }
          }
          return cart
        })
      }))
    },
    [ shop_id ]
  )

  const CartFacade: CartFacade = useMemo(
    () => ({
      carts: cartsHolder.carts,
      currentShopCart,
      currentShopID,
      setShopID: setCurrentShopID,
      setCarts,
      clearCurrentCart,
      addToCurrentCart,
      updateAmount,
      deleteFromCurrentCart,
      setCurrentCartProducts
    }),
    [
      addToCurrentCart,
      cartsHolder.carts,
      clearCurrentCart,
      currentShopCart,
      currentShopID,
      deleteFromCurrentCart,
      setCarts,
      setCurrentCartProducts,
      updateAmount
    ]
  )

  return CartFacade
}
